import React, { useState, useEffect } from 'react';
import { 
  FileBarChart, Download, Calendar, Shield, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Users, Ticket, HardDrive, GraduationCap,
  Building2, ChevronDown, RefreshCw, Clock, Target, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QBR = () => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [showQuarterPicker, setShowQuarterPicker] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchHistory();
    fetchReport();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/qbr/history`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch QBR history:', err);
    }
  };

  const fetchReport = async (quarter = null, year = null) => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/qbr/report`;
      const params = new URLSearchParams();
      if (quarter) params.append('quarter', quarter);
      if (year) params.append('year', year);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to fetch report');
      
      const data = await response.json();
      setReportData(data);
      setSelectedQuarter(data.quarter);
      setSelectedYear(data.year);
    } catch (err) {
      setError('Failed to load QBR data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      let url = `${API_URL}/api/qbr/download`;
      const params = new URLSearchParams();
      if (selectedQuarter) params.append('quarter', selectedQuarter);
      if (selectedYear) params.append('year', selectedYear);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `QBR_${selectedQuarter}_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download PDF report.');
    } finally {
      setDownloading(false);
    }
  };

  const handleQuarterSelect = (quarter, year) => {
    setShowQuarterPicker(false);
    fetchReport(quarter, year);
  };

  // Color based on percentage score instead of letter grade
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-blue-400';
    if (score >= 70) return 'text-amber-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30';
    if (score >= 80) return 'from-blue-500/20 to-blue-600/10 border-blue-500/30';
    if (score >= 70) return 'from-amber-500/20 to-amber-600/10 border-amber-500/30';
    if (score >= 60) return 'from-orange-500/20 to-orange-600/10 border-orange-500/30';
    return 'from-red-500/20 to-red-600/10 border-red-500/30';
  };

  const TrendIndicator = ({ value, reverse = false }) => {
    const isPositive = reverse ? value < 0 : value > 0;
    const isNegative = reverse ? value > 0 : value < 0;
    
    if (value === 0) return <span className="text-slate-400 text-xs">No change</span>;
    
    return (
      <span className={`flex items-center gap-1 text-xs ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-slate-400'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {Math.abs(value)}%
      </span>
    );
  };

  const StatCard = ({ icon: Icon, label, value, subValue, trend, trendReverse, color = 'violet' }) => (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        {trend !== undefined && <TrendIndicator value={trend} reverse={trendReverse} />}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-400">{label}</p>
      {subValue && <p className="text-xs text-slate-500 mt-1">{subValue}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading QBR data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => fetchReport()}
            className="mt-4 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { executiveSummary, securityPosture, vulnerabilityManagement, serviceDesk, training, vendorRisk } = reportData || {};

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="qbr-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <FileBarChart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Quarterly Business Review</h1>
                <p className="text-slate-400">{reportData?.organizationName}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Quarter Picker */}
            <div className="relative">
              <button
                onClick={() => setShowQuarterPicker(!showQuarterPicker)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white hover:bg-slate-700 transition-colors"
                data-testid="quarter-picker"
              >
                <Calendar className="w-4 h-4 text-violet-400" />
                <span>{selectedQuarter} {selectedYear}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showQuarterPicker ? 'rotate-180' : ''}`} />
              </button>

              {showQuarterPicker && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-2 z-50">
                  {history.map((h) => (
                    <button
                      key={`${h.quarter}-${h.year}`}
                      onClick={() => handleQuarterSelect(h.quarter, h.year)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-700/50 transition-colors ${
                        h.quarter === selectedQuarter && h.year === selectedYear
                          ? 'bg-violet-500/20 text-violet-300'
                          : 'text-slate-300'
                      }`}
                    >
                      {h.label}
                      {h.isCurrent && <span className="ml-2 text-xs text-violet-400">(Current)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => fetchReport(selectedQuarter, selectedYear)}
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-colors disabled:opacity-50"
              data-testid="download-pdf-btn"
            >
              <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
              {downloading ? 'Generating...' : 'Download PDF'}
            </button>
          </div>
        </div>

        {/* Period Info */}
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-400">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Report Period: {reportData?.periodStart} to {reportData?.periodEnd}
          </span>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className={`bg-gradient-to-br ${getScoreBg(executiveSummary?.overallHealthScore || 0)} rounded-2xl p-6 border`}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-shrink-0 text-center">
              <div className={`text-6xl font-bold ${getScoreColor(executiveSummary?.overallHealthScore || 0)}`}>
                {executiveSummary?.overallHealthScore || 0}%
              </div>
              <p className="text-slate-400 text-sm mt-1">Health Score</p>
            </div>
            
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-2">Executive Summary</h2>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-violet-400" />
                  <span className="text-white font-medium">Overall Health Score:</span>
                  <span className={`text-xl font-bold ${getScoreColor(executiveSummary?.overallHealthScore || 0)}`}>
                    {executiveSummary?.overallHealthScore}%
                  </span>
                </div>
              </div>

              {/* Highlights */}
              {executiveSummary?.highlights?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-emerald-400 font-medium mb-1">Highlights</p>
                  <ul className="space-y-1">
                    {executiveSummary.highlights.map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Concerns */}
              {executiveSummary?.concerns?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-amber-400 font-medium mb-1">Areas of Concern</p>
                  <ul className="space-y-1">
                    {executiveSummary.concerns.map((c, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {executiveSummary?.recommendations?.length > 0 && (
                <div>
                  <p className="text-sm text-blue-400 font-medium mb-1">Recommendations</p>
                  <ul className="space-y-1">
                    {executiveSummary.recommendations.map((r, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <Award className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Shield}
          label="Compliance Score"
          value={`${securityPosture?.overallScore || 0}%`}
          subValue={`${securityPosture?.completedAssessments || 0}/${securityPosture?.totalAssessments || 0} assessments completed`}
          color="violet"
        />
        <StatCard
          icon={AlertTriangle}
          label="Vulnerability Remediation"
          value={`${vulnerabilityManagement?.remediationRate || 0}%`}
          subValue={`${vulnerabilityManagement?.resolvedAlerts || 0} resolved / ${vulnerabilityManagement?.totalAlerts || 0} total`}
          color="amber"
        />
        <StatCard
          icon={Ticket}
          label="SLA Attainment"
          value={`${serviceDesk?.slaAttainmentRate || 0}%`}
          subValue={serviceDesk?.avgResolutionTimeFormatted ? `Avg resolution: ${serviceDesk.avgResolutionTimeFormatted}` : undefined}
          color="cyan"
        />
        <StatCard
          icon={GraduationCap}
          label="Training Completion"
          value={`${training?.avgCompletionRate || 0}%`}
          subValue={`${training?.activeCampaigns || 0} active campaigns`}
          color="emerald"
        />
      </div>

      {/* Detailed Sections */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Security & Compliance */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50" data-testid="security-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Security & Compliance</h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Total Assessments</span>
              <span className="text-white font-medium">{securityPosture?.totalAssessments || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Completed</span>
              <span className="text-emerald-400 font-medium">{securityPosture?.completedAssessments || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">In Progress</span>
              <span className="text-amber-400 font-medium">{securityPosture?.inProgressAssessments || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-400">Security Score</span>
              <span className={`text-xl font-bold ${getScoreColor(securityPosture?.score || 0)}`}>{securityPosture?.score || 0}%</span>
            </div>

            {/* Framework Breakdown */}
            {securityPosture?.byFramework && Object.keys(securityPosture.byFramework).length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-sm text-slate-400 mb-2">By Framework</p>
                <div className="space-y-2">
                  {Object.entries(securityPosture.byFramework).map(([fw, data]) => (
                    <div key={fw} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300">{fw}</span>
                      <span className="text-white">{data.count} ({data.avgProgress?.toFixed(1) || 0}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Vulnerability Management */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50" data-testid="vulnerability-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Vulnerability Management</h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Assets Monitored</span>
              <span className="text-white font-medium">{vulnerabilityManagement?.totalAssets || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Critical Assets</span>
              <span className="text-red-400 font-medium">{vulnerabilityManagement?.criticalAssets || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Open Alerts</span>
              <span className="text-amber-400 font-medium">{vulnerabilityManagement?.openAlerts || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-400">Resolved</span>
              <span className="text-emerald-400 font-medium">{vulnerabilityManagement?.resolvedAlerts || 0}</span>
            </div>

            {/* Priority Distribution */}
            {vulnerabilityManagement?.priorityDistribution && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-sm text-slate-400 mb-2">Open by Priority</p>
                <div className="grid grid-cols-4 gap-2">
                  {['P1', 'P2', 'P3', 'P4'].map((p) => (
                    <div key={p} className={`text-center p-2 rounded-lg ${
                      p === 'P1' ? 'bg-red-500/20' :
                      p === 'P2' ? 'bg-orange-500/20' :
                      p === 'P3' ? 'bg-amber-500/20' : 'bg-slate-700/50'
                    }`}>
                      <p className={`text-lg font-bold ${
                        p === 'P1' ? 'text-red-400' :
                        p === 'P2' ? 'text-orange-400' :
                        p === 'P3' ? 'text-amber-400' : 'text-slate-400'
                      }`}>{vulnerabilityManagement.priorityDistribution[p] || 0}</p>
                      <p className="text-xs text-slate-400">{p}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Affected Vendors */}
            {vulnerabilityManagement?.topAffectedVendors?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-sm text-slate-400 mb-2">Top Affected Vendors</p>
                <div className="space-y-1">
                  {vulnerabilityManagement.topAffectedVendors.slice(0, 3).map((v, i) => (
                    <div key={i} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300">{v.vendor}</span>
                      <span className="text-amber-400">{v.count} alerts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* IT Service Desk */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50" data-testid="servicedesk-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">IT Service Desk ({selectedQuarter})</h3>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Total Tickets</span>
              <span className="text-white font-medium">{serviceDesk?.totalTickets || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Resolved</span>
              <span className="text-emerald-400 font-medium">{serviceDesk?.resolvedTickets || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Avg Response Time</span>
              <span className="text-white font-medium">{serviceDesk?.avgResponseTimeFormatted || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-400">Avg Resolution Time</span>
              <span className="text-white font-medium">{serviceDesk?.avgResolutionTimeFormatted || 'N/A'}</span>
            </div>

            {/* Ticket Type Distribution */}
            {serviceDesk?.byType && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-sm text-slate-400 mb-2">By Type</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'incident', label: 'Incidents', color: 'red' },
                    { key: 'service_request', label: 'Requests', color: 'blue' },
                    { key: 'problem', label: 'Problems', color: 'amber' },
                    { key: 'change', label: 'Changes', color: 'emerald' }
                  ].map(({ key, label, color }) => (
                    <div key={key} className={`text-center p-2 rounded-lg bg-${color}-500/10`}>
                      <p className={`text-lg font-bold text-${color}-400`}>{serviceDesk.byType[key] || 0}</p>
                      <p className="text-xs text-slate-400">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Training & Vendor Risk */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50" data-testid="training-vendor-section">
          <div className="space-y-6">
            {/* Training */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Security Training</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total Campaigns</span>
                  <span className="text-white">{training?.totalCampaigns || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Active</span>
                  <span className="text-emerald-400">{training?.activeCampaigns || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Avg Completion</span>
                  <span className="text-white font-medium">{training?.avgCompletionRate || 0}%</span>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-700/50"></div>

            {/* Vendor Risk */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Vendor Risk</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total Vendors</span>
                  <span className="text-white">{vendorRisk?.totalVendors || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">High Risk</span>
                  <span className="text-red-400">{vendorRisk?.highRiskVendors || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Assessed</span>
                  <span className="text-emerald-400">{vendorRisk?.assessedVendors || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 text-center text-sm text-slate-500">
        <p>Report generated on {new Date(reportData?.generatedAt).toLocaleString()}</p>
        <p className="mt-1">Cortex Cybersecurity Platform - {reportData?.organizationName}</p>
      </div>
    </div>
  );
};

export default QBR;
